if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        dots: this.querySelector('.bpc-mobile-gallery-dots__track')
      };
      this.mql = window.matchMedia('(min-width: 750px)');
      this.slideObserver = null;
      this.thumbnailObserver = null;
      this.thumbnailMediaListener = this.setupThumbnailHydration.bind(this);

      if (!this.elements.viewer) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 120));

      if (this.elements.thumbnails) {
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button');
          if (button) button.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
      }

      this.querySelectorAll('[data-bpc-gallery-dot]').forEach((dot) => {
        dot.addEventListener('click', () => this.setActiveMedia(dot.dataset.target, false));
      });

      this.setupDeferredMedia();

      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
    }

    disconnectedCallback() {
      if (this.slideObserver) this.slideObserver.disconnect();
      if (this.thumbnailObserver) this.thumbnailObserver.disconnect();
      if (this.mql.removeEventListener) this.mql.removeEventListener('change', this.thumbnailMediaListener);
      else if (this.mql.removeListener) this.mql.removeListener(this.thumbnailMediaListener);
    }

    get sliderElement() {
      return this.elements.viewer.slider || this.elements.viewer.querySelector('[id^="Slider-Gallery"]');
    }

    get slides() {
      return Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'));
    }

    setupDeferredMedia() {
      const activeSlide = this.elements.viewer.querySelector('[data-media-id].is-active') || this.slides[0];
      this.hydrateContainer(activeSlide);
      this.observeSlides();
      this.setupThumbnailHydration();

      const hydrateNeighbor = () => this.hydrateAdjacent(activeSlide, 1);
      if ('requestIdleCallback' in window) window.requestIdleCallback(hydrateNeighbor, { timeout: 1800 });
      else window.setTimeout(hydrateNeighbor, 1200);
    }

    observeSlides() {
      if (!('IntersectionObserver' in window)) return;
      const root = this.sliderElement;
      if (!root) return;

      this.slideObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.hydrateContainer(entry.target);
        });
      }, {
        root,
        rootMargin: '0px 85% 0px 85%',
        threshold: 0.01
      });

      this.slides.forEach((slide) => this.slideObserver.observe(slide));
    }

    setupThumbnailHydration() {
      if (this.thumbnailObserver) {
        this.thumbnailObserver.disconnect();
        this.thumbnailObserver = null;
      }

      if (!this.elements.thumbnails || !this.mql.matches) {
        if (this.mql.addEventListener) {
          this.mql.removeEventListener('change', this.thumbnailMediaListener);
          this.mql.addEventListener('change', this.thumbnailMediaListener);
        } else if (this.mql.addListener) {
          this.mql.removeListener(this.thumbnailMediaListener);
          this.mql.addListener(this.thumbnailMediaListener);
        }
        return;
      }

      const items = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
      items.slice(0, 6).forEach((item) => this.hydrateContainer(item));

      if ('IntersectionObserver' in window) {
        const root = this.elements.thumbnails.slider || this.elements.thumbnails.querySelector('.thumbnail-list');
        this.thumbnailObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.hydrateContainer(entry.target);
          });
        }, { root: root || null, rootMargin: '0px 100% 0px 100%', threshold: 0.01 });
        items.forEach((item) => this.thumbnailObserver.observe(item));
      } else {
        items.forEach((item) => this.hydrateContainer(item));
      }

      if (this.mql.addEventListener) {
        this.mql.removeEventListener('change', this.thumbnailMediaListener);
        this.mql.addEventListener('change', this.thumbnailMediaListener);
      } else if (this.mql.addListener) {
        this.mql.removeListener(this.thumbnailMediaListener);
        this.mql.addListener(this.thumbnailMediaListener);
      }
    }

    hydrateImage(image) {
      if (!image || !image.dataset) return;
      const src = image.dataset.bpcSrc;
      const srcset = image.dataset.bpcSrcset;
      if (!src && !srcset) return;

      if (srcset) image.srcset = srcset;
      if (src) image.src = src;
      delete image.dataset.bpcSrc;
      delete image.dataset.bpcSrcset;
      image.removeAttribute('data-bpc-deferred-image');
      image.removeAttribute('data-bpc-gallery-thumbnail');
      image.classList.add('bpc-image-hydrated');
    }

    hydrateContainer(container) {
      if (!container) return;
      if (container.matches && container.matches('img[data-bpc-src], img[data-bpc-srcset]')) this.hydrateImage(container);
      container.querySelectorAll('img[data-bpc-src], img[data-bpc-srcset]').forEach((image) => this.hydrateImage(image));
    }

    hydrateAdjacent(activeSlide, distance = 1) {
      if (!activeSlide) return;
      const slides = this.slides;
      const activeIndex = slides.indexOf(activeSlide);
      if (activeIndex < 0) return;

      for (let offset = -distance; offset <= distance; offset += 1) {
        const slide = slides[activeIndex + offset];
        if (slide) this.hydrateContainer(slide);
      }
    }

    onSlideChanged(event) {
      const current = event.detail && event.detail.currentElement;
      if (!current) return;
      this.hydrateAdjacent(current, 1);
      const thumbnail = this.elements.thumbnails
        ? this.elements.thumbnails.querySelector(`[data-target="${current.dataset.mediaId}"]`)
        : null;
      this.setActiveThumbnail(thumbnail);
      this.setActiveDot(current.dataset.mediaId);
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
      if (!activeMedia) return;

      this.hydrateAdjacent(activeMedia, 1);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          if (activeThumbnail) activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        const slider = this.sliderElement || activeMedia.parentElement;
        if (slider && slider.scrollTo) {
          slider.scrollTo({
            left: activeMedia.offsetLeft,
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
          });
        }
      });
      this.playActiveMedia(activeMedia);

      const activeThumbnail = this.elements.thumbnails
        ? this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`)
        : null;
      this.setActiveThumbnail(activeThumbnail);
      this.setActiveDot(mediaId);

      const positionSource = activeThumbnail || this.querySelector(`[data-bpc-gallery-dot][data-target="${mediaId}"]`);
      if (positionSource) this.announceLiveRegion(activeMedia, positionSource.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.hydrateContainer(thumbnail);
      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      const button = thumbnail.querySelector('button');
      if (button) button.setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible && this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      const slider = this.elements.thumbnails.slider || this.elements.thumbnails.querySelector('.thumbnail-list');
      if (slider && slider.scrollTo) slider.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
    }

    setActiveDot(mediaId) {
      const dots = Array.from(this.querySelectorAll('[data-bpc-gallery-dot]'));
      if (!dots.length) return;
      let activeDot = null;
      dots.forEach((dot) => {
        const active = dot.dataset.target === mediaId;
        dot.classList.toggle('is-active', active);
        if (active) {
          dot.setAttribute('aria-current', 'true');
          activeDot = dot;
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      if (activeDot && this.elements.dots) {
        const left = activeDot.offsetLeft - (this.elements.dots.clientWidth - activeDot.offsetWidth) / 2;
        this.elements.dots.scrollTo({
          left: Math.max(0, left),
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
        });
      }
    }

    announceLiveRegion(activeItem, position) {
      if (!this.elements.liveRegion || !position) return;
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;

      const announce = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
        window.setTimeout(() => this.elements.liveRegion.setAttribute('aria-hidden', true), 2000);
      };

      if (image.complete) announce();
      else image.addEventListener('load', announce, { once: true });
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
    }
  });
}
